/**
 * Widget Configuration Validation Schemas
 * Zod schemas for client-side validation matching backend rules
 */

import { z } from 'zod';
import { CONTAINER_SIZES, FONT_FAMILIES } from '../types/widget-config';

// Helper validators
const hexColorRegex = /^#[0-9A-Fa-f]{6}$/;
const urlOrEmpty = z.string().url().optional().or(z.literal(''));

// Branding Schema
export const BrandingSchema = z.object({
  companyName: z.string().min(1, 'Company name is required').max(100, 'Company name too long'),
  logoUrl: urlOrEmpty,
  primaryColor: z.string().regex(hexColorRegex, 'Must be valid hex color (e.g. #3b82f6)'),
  secondaryColor: z.string().regex(hexColorRegex, 'Must be valid hex color').optional().or(z.literal('')),
  fontFamily: z.enum(FONT_FAMILIES as unknown as readonly [string, ...string[]]).optional(),
  heroImage: urlOrEmpty,
});

export type BrandingFormData = z.infer<typeof BrandingSchema>;

// Service Offering Schema
export const ServiceOfferingSchema = z.object({
  containerSize: z.enum(CONTAINER_SIZES as unknown as readonly [string, ...string[]], {
    errorMap: () => ({ message: 'Invalid container size' }),
  }),
  description: z.string().min(1, 'Description is required').max(200, 'Description too long'),
  available: z.boolean(),
  capacity: z.number().min(0, 'Capacity cannot be negative').optional(),
});

// Services Schema
export const ServicesSchema = z.object({
  services: z.array(ServiceOfferingSchema).min(1, 'At least one service required'),
});

export type ServicesFormData = z.infer<typeof ServicesSchema>;

// Pricing Schema
export const PricingSchema = z.object({
  basePricing: z.record(z.number().min(0, 'Price must be positive')),
  deliveryFee: z.number().min(0, 'Delivery fee must be positive').optional(),
  disposalFees: z.record(z.number().min(0, 'Disposal fee must be positive')).optional(),
  rentalPeriodDays: z.number().min(1, 'Rental period must be at least 1 day').max(365, 'Rental period too long'),
  overageFeePerDay: z.number().min(0, 'Overage fee must be positive').optional(),
  environmentalFee: z.number().min(0, 'Environmental fee must be positive').optional(),
  taxRate: z.number().min(0, 'Tax rate cannot be negative').max(1, 'Tax rate must be between 0 and 1').optional(),
});

export type PricingFormData = z.infer<typeof PricingSchema>;

// Business Info Schema
export const BusinessInfoSchema = z.object({
  contactEmail: z.string().email('Invalid email format'),
  contactPhone: z.string().min(10, 'Phone must be at least 10 digits').max(20, 'Phone number too long'),
  website: urlOrEmpty,
  businessAddress: z.string().max(200, 'Address too long').optional().or(z.literal('')),
  businessHours: z.string().max(200, 'Business hours too long').optional().or(z.literal('')),
  licenseNumber: z.string().max(50, 'License number too long').optional().or(z.literal('')),
});

export type BusinessInfoFormData = z.infer<typeof BusinessInfoSchema>;

// Embed Settings Schema
export const EmbedSettingsSchema = z.object({
  allowedDomains: z.array(z.string()).min(1, 'At least one domain required'),
  defaultWidth: z.string().optional(),
  defaultHeight: z.string().optional(),
  analyticsEnabled: z.boolean(),
});

export type EmbedSettingsFormData = z.infer<typeof EmbedSettingsSchema>;

// File upload validation
export const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];

export function validateImageFile(file: File): { valid: boolean; error?: string } {
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: 'File too large. Maximum size is 5MB.' };
  }

  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    return { valid: false, error: 'Invalid file type. Use PNG, JPG, SVG, or WebP.' };
  }

  return { valid: true };
}

// Helper function to convert color to hex format
export function normalizeHexColor(color: string): string {
  const cleaned = color.trim();
  if (cleaned.startsWith('#')) {
    return cleaned.toUpperCase();
  }
  return `#${cleaned.toUpperCase()}`;
}

// Helper function to format phone number
export function formatPhoneNumber(phone: string): string {
  // Remove all non-digits
  const digits = phone.replace(/\D/g, '');

  // Format as (XXX) XXX-XXXX for 10-digit numbers
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  return phone; // Return as-is if not 10 digits
}
